/**
 * Global recording status (cdb_webos#393).
 *
 * Local in-app indicators (waveform, timer) die with the window focus — a
 * minimized recorder is invisible. Every active audio recording registers
 * here, and the Taskbar renders a persistent red REC chip that is visible
 * regardless of which window has focus; clicking it focuses the recording
 * window. Multiple simultaneous recordings are supported (chip shows the
 * oldest; the count covers the rest).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export interface ActiveRecording {
  /** Stable id per recording registration (caller-chosen, e.g. session id). */
  id: string
  /** App that records — drives the chip label ('ai-knowledge-extractor' | 'cdb-sessions'). */
  appId: string
  /** Window to focus when the chip is clicked (undefined = no-op). */
  windowId?: string
  startedAt: number
}

interface RecordingStatusContextValue {
  recordings: ActiveRecording[]
  registerRecording: (recording: ActiveRecording) => void
  unregisterRecording: (id: string) => void
}

const RecordingStatusContext = createContext<RecordingStatusContextValue | null>(null)

export function RecordingStatusProvider({ children }: { children: ReactNode }) {
  const [recordings, setRecordings] = useState<ActiveRecording[]>([])

  const registerRecording = useCallback((recording: ActiveRecording) => {
    setRecordings((prev) => [
      ...prev.filter((r) => r.id !== recording.id),
      recording,
    ])
  }, [])

  const unregisterRecording = useCallback((id: string) => {
    setRecordings((prev) => prev.filter((r) => r.id !== id))
  }, [])

  const value = useMemo(
    () => ({ recordings, registerRecording, unregisterRecording }),
    [recordings, registerRecording, unregisterRecording],
  )

  return (
    <RecordingStatusContext.Provider value={value}>
      {children}
    </RecordingStatusContext.Provider>
  )
}

/** Null-safe accessor: components render fine outside the provider (tests). */
export function useRecordingStatus(): RecordingStatusContextValue {
  const ctx = useContext(RecordingStatusContext)
  return (
    ctx ?? {
      recordings: [],
      registerRecording: () => {},
      unregisterRecording: () => {},
    }
  )
}
